import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, apiError, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export const STAFF_ROLES = ["staff", "admin", "investigator"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      const { data } = await api.post("/auth/login", { username, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const status = e.response?.status;
      const fallback =
        status === 503
          ? "We can't establish a secure connection to the case system right now, so sign-in is temporarily unavailable. Please try again shortly."
          : "Unable to sign in";
      return { ok: false, status, error: apiError(e.response?.data?.detail, fallback) };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const isStaff = !!user && STAFF_ROLES.includes(user.role);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isStaff }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
